import isEqual from 'lodash/isEqual'
import PropTypes from 'prop-types'
import React, { useCallback, useRef } from 'react'
import Selectable from 'react-select'
import ErrorLabel from 'ui/atoms/ErrorLabel'

import Ui from '.'

export const selectTheme = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    danger: '#F05252',
    primary: '#1d8cf8',
    primary75: '#68b2fa',
    primary50: '#8ec6fc',
    primary25: '#b4d9fd',
    neutral50: 'rgb(159,166,178)',
    neutral80: 'rgb(3,21,51)',
  },
})

function SelectGroup({
  label,
  disabled,
  loading,
  options,
  value,
  target,
  onChange,
  placeholder,
  valueProp,
  hasError,
  errorLabel,
  menuPosition,
  menuPlacement,
  isMulti = false,
  block,
}) {
  const selectableRef = useRef()

  const valuePropToUse = valueProp || 'value'

  const handleChange = React.useCallback(
    (option) => {
      onChange(option)
    },
    [onChange]
  )

  const styles = useCallback(
    () => ({
      control: (provided) => ({
        ...provided,
        ...(hasError ? { borderColor: '#F05252' } : {}),
        '&:hover': {
          ...provided?.['&:hover'],
          ...(hasError ? { borderColor: '#F05252' } : {}),
        },
      }),
      menuList: (provided) => ({
        ...provided,
        maxHeight: '195px',
      }),
      input: (provided) => ({
        ...provided,
        '& > div > input': {
          opacity: '1 !important',
        },
      }),
    }),
    [hasError]
  )

  const renderContent = React.useCallback(
    () => (
      <>
        {label && <Ui.QuestionTitle>{label}</Ui.QuestionTitle>}
        <Selectable
          isClearable
          className="rounded-md sm:text-sm sm:leading-5 transition duration-150 ease-in-out"
          getOptionValue={(option) => option[valuePropToUse]}
          isDisabled={disabled}
          isLoading={loading}
          isMulti={isMulti}
          menuPlacement={menuPlacement ?? 'auto'}
          menuPosition={menuPosition || 'absolute'}
          noOptionsMessage={() => null}
          onChange={handleChange}
          options={options}
          placeholder={placeholder}
          ref={selectableRef}
          styles={styles()}
          theme={selectTheme}
          value={value}
        />
        {hasError && <ErrorLabel>{errorLabel}</ErrorLabel>}
      </>
    ),
    [
      disabled,
      errorLabel,
      handleChange,
      hasError,
      isMulti,
      label,
      loading,
      menuPlacement,
      menuPosition,
      options,
      placeholder,
      styles,
      value,
      valuePropToUse,
    ]
  )

  if (block) {
    return <div>{renderContent()}</div>
  }

  return renderContent()
}

SelectGroup.defaultProps = {
  onChange: () => {},
  options: [],
  showOptions: false,
}

SelectGroup.propTypes = {
  hasError: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.any,
  label: PropTypes.string,
  target: PropTypes.string,
  placeholder: PropTypes.string,
  valueProp: PropTypes.string,
  options: PropTypes.array.isRequired,
  labelFormatter: PropTypes.func,
  selectedLabelFormatter: PropTypes.func,
  LabelRenderer: PropTypes.func,
  errorLabel: PropTypes.string,
  showOptions: PropTypes.bool,
  menuPlacement: PropTypes.oneOf(['auto', 'top', 'bottom']),
  blockCreate: PropTypes.bool,
}

function areEqual(a, b) {
  return (
    a.label === b.label &&
    a.target === b.target &&
    a.hasError === b.hasError &&
    a.errorLabel === b.errorLabel &&
    a.disabled === b.disabled &&
    a.loading === b.loading &&
    isEqual(a.options, b.options) &&
    isEqual(a.value, b.value)
  )
}

export default React.memo(SelectGroup, areEqual)
